import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Flower2, X, Send, Loader2, Sparkles } from "lucide-react";
import { loadDeliveryInfo } from "../lib/checkoutStorage";
import { getCookieConsent } from "./CookieBanner";
import { thumb } from "../lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "fl_flora_session";
const HIDDEN_PREFIXES = ["/admin", "/dashboard"];
const WELCOME = "Ciao, sono Flora 🌸 Ti aiuto a trovare i fiori giusti in due minuti. Dimmi: per chi sono?";
const QUICK = ["Per una persona cara", "Per la mamma", "Per me", "Un'occasione speciale", "Come funziona la consegna?"];
const FLORIST_WELCOME = "Ciao, sono Flora 🌸 Ti accompagno nell'apertura del negozio: se un passo non ti è chiaro, chiedimi pure. Salviamo tutto, puoi fermarti e riprendere quando vuoi.";
const FLORIST_QUICK = { 1: ["Quanto costa?", "Perché serve la partita IVA?", "Posso farlo dal telefono?"], 2: ["Come scelgo l'indirizzo web?", "Posso cambiarlo dopo?"], 3: ["Non ho il logo, che faccio?", "Che foto va bene per il banner?"], 4: ["Posso chiudere per ferie?", "Orario continuato?"], 5: ["Chi consegna i fiori?", "Posso cambiare le tariffe?"], 6: ["Devo fare io le foto?", "Posso cambiare i prezzi?", "Cosa sono le tre misure?"], 7: ["Cos'è Stripe?", "Cosa succede dopo la pubblicazione?"] };

const renderInline = (text) => {
  const parts = text.split(/(\[[^\]]+\]\((?:\/|https?:\/\/)[^)]*\)|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return link[2].startsWith("/") ? <Link key={i} to={link[2]} className="underline text-forest font-medium">{link[1]}</Link> : <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="underline text-forest font-medium">{link[1]}</a>;
    const bold = p.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return p;
  });
};

const ProductCard = ({ p }) => (
  <Link to={`/products/${p.id}`} className="flex items-center gap-3 rounded-xl border border-cream-dark bg-white p-2 hover:border-forest/50 transition-colors" data-testid={`flora-product-${p.id}`}>
    <img src={thumb(p.image, 160) || "/product-placeholder.svg"} alt="" className="h-14 w-14 rounded-lg object-cover bg-cream shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-forest truncate">{p.name}</p>
      <p className="text-xs text-sage">da €{Number(p.price).toFixed(2)}{p.shop_name ? ` · ${p.shop_name}` : ""}</p>
    </div>
    <span className="text-[11px] font-semibold text-white bg-forest rounded-full px-2.5 py-1 shrink-0">Vedi</span>
  </Link>
);

const Bubble = ({ m, products }) => {
  const lines = m.content.split(/\n/).filter(l => l.trim() !== "");
  return (
    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`flora-msg-${m.role}`}>
      <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed space-y-2 ${m.role === "user" ? "bg-forest text-white rounded-br-md" : "bg-white border border-cream-dark text-forest rounded-bl-md"}`}>
        {lines.map((l, i) => {
          const tag = l.match(/\[\[product:([a-f0-9]{24})\]\]/);
          if (tag) return products[tag[1]] ? <ProductCard key={i} p={products[tag[1]]} /> : (products[tag[1]] === null ? null : <div key={i} className="h-16 rounded-xl bg-cream animate-pulse" />);
          return <p key={i}>{renderInline(l.replace(/^[-•]\s*/, ""))}</p>;
        })}
        {m.streaming && lines.length === 0 && <Loader2 className="h-4 w-4 animate-spin text-sage" />}
      </div>
    </div>
  );
};

export const FloraChat = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const shrink = (e) => { if (!e.target?.closest?.("[data-testid='flora-open']")) setCompact(true); };
    const onScroll = () => { if (window.scrollY > 80) setCompact(true); };
    window.addEventListener("pointerdown", shrink, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("pointerdown", shrink); window.removeEventListener("scroll", onScroll); };
  }, []);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: WELCOME }]);
  const floristMode = location.pathname.startsWith("/apri-negozio");
  const [obStep, setObStep] = useState(1);
  useEffect(() => {
    const onStep = (e) => setObStep(e.detail?.step || 1);
    window.addEventListener("fl-onboarding-step", onStep);
    return () => window.removeEventListener("fl-onboarding-step", onStep);
  }, []);
  useEffect(() => { setMsgs(m => (m.length === 1 ? [{ role: "assistant", content: floristMode ? FLORIST_WELCOME : WELCOME }] : m)); }, [floristMode]);
  const quick = floristMode ? (FLORIST_QUICK[obStep] || FLORIST_QUICK[1]) : QUICK;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState({});
  const [ctx, setCtx] = useState({});
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || "");
  const [consentPending, setConsentPending] = useState(() => !getCookieConsent());
  const endRef = useRef(null);

  useEffect(() => {
    const onConsent = () => setConsentPending(!getCookieConsent());
    window.addEventListener("fl:cookie-consent", onConsent);
    return () => window.removeEventListener("fl:cookie-consent", onConsent);
  }, []);

  useEffect(() => {
    const onShop = (e) => setCtx(c => ({ ...c, ...e.detail }));
    window.addEventListener("fl-shop-viewed", onShop);
    return () => window.removeEventListener("fl-shop-viewed", onShop);
  }, []);
  useEffect(() => { if (!location.pathname.startsWith("/fioristi") && !location.pathname.startsWith("/shops")) setCtx(c => ({ ...c, shop_id: undefined })); }, [location.pathname]);
  useEffect(() => {
    if (!open || !sid || msgs.length > 1) return;
    fetch(`${API}/assistant/history/${sid}`).then(r => r.json()).then(d => {
      if (d.messages?.length) { setMsgs(d.messages); loadProducts(d.messages.map(m => m.content).join("\n")); }
    }).catch(() => {});
  }, [open, sid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const loadProducts = async (text) => {
    const ids = [...new Set([...text.matchAll(/\[\[product:([a-f0-9]{24})\]\]/g)].map(m => m[1]))].filter(id => products[id] === undefined);
    if (!ids.length) return;
    try {
      const r = await fetch(`${API}/assistant/products?ids=${ids.join(",")}`); const data = await r.json();
      setProducts(p => ({ ...p, ...Object.fromEntries(ids.map(id => [id, data[id] || null])) }));
    } catch { setProducts(p => ({ ...p, ...Object.fromEntries(ids.map(id => [id, null])) })); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput(""); setBusy(true);
    setMsgs(m => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    const delivery = loadDeliveryInfo() || {};
    const cityFromPath = location.pathname.match(/^\/fiori-a-([a-z0-9-]+)/)?.[1]?.replace(/-/g, " ");
    try {
      const res = await fetch(`${API}/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid || undefined, message: msg, path: floristMode ? `/apri-negozio?step=${obStep}` : location.pathname, shop_id: ctx.shop_id || delivery.shop_id, city: ctx.city || delivery.city || cityFromPath }) });
      if (!res.ok || !res.body) throw new Error("http");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = ""; let full = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n"); buf = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find(l => l.startsWith("data: "));
          if (!line) continue;
          const d = JSON.parse(line.slice(6));
          if (d.delta) { full += d.delta; setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: full, streaming: true }; return c; }); }
          if (d.done) { if (d.session_id && d.session_id !== sid) { setSid(d.session_id); localStorage.setItem(SID_KEY, d.session_id); } loadProducts(full); }
        }
      }
      setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: full }; return c; });
    } catch {
      setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Scusami, non riesco a rispondere in questo momento. Riprova tra poco o scrivici a info@flowerlive.it." }; return c; });
    } finally { setBusy(false); }
  };

  if (HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p))) return null;

  return (
    <>
      {!open && !consentPending && (
        <button onClick={() => setOpen(true)} aria-label="Chiedi a Flora" className={`fixed right-4 z-[60] flex items-center gap-2 rounded-full bg-forest text-white pl-3 py-2.5 ${location.pathname.startsWith("/products/") ? "bottom-24 lg:bottom-5" : "bottom-5"} shadow-lg shadow-forest/30 hover:bg-forest-light transition-[transform,background-color] hover:-translate-y-0.5 ${compact ? "pr-3 sm:pr-4" : "pr-4"}`} data-testid="flora-open">
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/15"><Flower2 className="h-4.5 w-4.5" /><span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-brand-pink border-2 border-forest" /></span>
          <span className={`text-sm font-medium ${compact ? "hidden sm:inline" : ""}`}>Chiedi a Flora</span>
        </button>
      )}
      {open && (
        <div className="fixed z-[60] inset-0 sm:inset-auto sm:bottom-5 sm:right-4 sm:w-[380px] sm:h-[600px] sm:max-h-[calc(100vh-40px)] flex flex-col bg-cream sm:rounded-3xl shadow-2xl border border-cream-dark overflow-hidden" data-testid="flora-panel">
          <div className="flex items-center gap-3 px-4 py-3 bg-forest text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15"><Flower2 className="h-5 w-5" /></span>
            <div className="flex-1 min-w-0"><p className="font-medium leading-tight">Flora</p><p className="text-[11px] text-white/75 leading-tight">{floristMode ? "Ti guido nell'apertura del negozio" : "La tua consulente floreale · risponde subito"}</p></div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/15" aria-label="Chiudi" data-testid="flora-close"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="flora-messages">
            {msgs.map((m, i) => <Bubble key={i} m={m} products={products} />)}
            {msgs.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="flora-quick">
                {quick.map(q => <button key={q} onClick={() => send(q)} className="text-xs rounded-full border border-forest/30 bg-white px-3 py-1.5 text-forest hover:bg-forest hover:text-white transition-colors">{q}</button>)}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={e => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 bg-white border-t border-cream-dark">
            <input value={input} onChange={e => setInput(e.target.value)} placeholder="Scrivi qui, come al banco del fiorista…" className="flex-1 h-10 rounded-full border border-cream-dark px-4 text-sm text-forest outline-none focus:border-forest" data-testid="flora-input" />
            <button type="submit" disabled={busy || !input.trim()} className="h-10 w-10 rounded-full bg-forest text-white flex items-center justify-center disabled:opacity-50" aria-label="Invia" data-testid="flora-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
          <p className="text-[10px] text-sage text-center pb-1.5 bg-white flex items-center justify-center gap-1"><Sparkles className="h-3 w-3" />Consigli generati con AI: prezzi e disponibilità si confermano in pagina</p>
        </div>
      )}
    </>
  );
};
